import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DeletableEdge from "./DeletableEdge";
import FlowSimulator from "./FlowSimulator";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
	ReactFlow,
	Background,
	Controls,
	MiniMap,
	addEdge,
	useNodesState,
	useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
	ArrowLeft,
	Save,
	Play,
	History,
	Trash2,
	Copy,
	MessageSquare,
	Clock,
	GitBranch,
	Globe,
	Loader2,
	RefreshCw,
  Sparkles,
  Tag,
  Users,
  StickyNote,
  ListOrdered,
  HelpCircle,
  MousePointerClick,
  List as ListIcon,
  Flag,
  Image,
  Shuffle,
  Upload,
  Download,
  Variable,
  CornerUpLeft,
  UserCheck,
  CircleCheck,
  Plus,
  Layers,
  Plug,
  Mic,
  Video,
  Trash,
  Timer,
  Ban,
  Columns3,
} from "lucide-react";

import { exportFlowToJson, processImportedFlowData } from "../../helpers/flowExportImport";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../../components/ui/dialog";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from "../../components/ui/sheet";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import { getBackendUrl } from "../../config";
import { nodeTypes, NODE_DEFAULTS, NODE_LABELS, ACENTOS } from "./nodes";
import { cn } from "../../lib/utils";

// Edge types: registrado fora do componente para não recriar a cada render.
// Mapeamos todos os tipos para DeletableEdge para garantir que qualquer
// conexão (antiga ou nova) exiba o botão de lixeira.
const edgeTypes = {
	deletable: DeletableEdge,
	default: DeletableEdge,
	smoothstep: DeletableEdge,
	step: DeletableEdge,
	straight: DeletableEdge,
};

// Agrupada por intenção: primeiro o que fala com o cliente, depois o que
// decide o caminho, e por fim o que mexe no atendimento. Uma lista corrida de
// treze itens obrigaria a ler tudo para achar um.
const PALETTE = [
	{
		grupo: "O que o robô diz",
		acento: "fala",
		itens: [
			{ type: "content", label: "Conteúdo", icon: Layers },
			{ type: "media", label: "Enviar mídia", icon: Image },
			{ type: "sendMessage", label: "Avisar outro número", icon: MessageSquare },
			{ type: "ai", label: "Inteligência Artificial", icon: Sparkles },
		],
	},
	{
		grupo: "O que o robô pergunta",
		acento: "pergunta",
		itens: [
			{ type: "menu", label: "Menu", icon: ListOrdered },
			{ type: "buttons", label: "Botões", icon: MousePointerClick },
			{ type: "list", label: "Lista", icon: ListIcon },
			{ type: "question", label: "Pergunta", icon: HelpCircle },
		],
	},
	{
		grupo: "Por onde a conversa segue",
		acento: "caminho",
		itens: [
			{ type: "condition", label: "Condição", icon: GitBranch },
			{ type: "randomizer", label: "Sorteio", icon: Shuffle },
			{ type: "delay", label: "Atraso", icon: Clock },
			{ type: "setVariable", label: "Definir variável", icon: Variable },
			{ type: "jump", label: "Pular para", icon: CornerUpLeft },
		],
	},
	{
		grupo: "Atendimento",
		acento: "atendimento",
		itens: [
			{ type: "followUp", label: "Follow-up", icon: Timer },
			{ type: "transferQueue", label: "Transferir para fila", icon: Users },
			{ type: "assignUser", label: "Atribuir atendente", icon: UserCheck },
			{ type: "createDeal", label: "Criar Oportunidade (CRM)", icon: UserCheck },
			{ type: "ticketStatus", label: "Situação", icon: CircleCheck },
			{ type: "addTag", label: "Aplicar etiqueta", icon: Tag },
			{ type: "note", label: "Nota interna", icon: StickyNote },
			{ type: "board", label: "Mover no quadro", icon: Columns3 },
			{ type: "end", label: "Fim", icon: Flag },
		],
	},
	{
		grupo: "O que o robô faz na conversa",
		acento: "atendimento",
		itens: [
			{ type: "typing", label: "Digitando", icon: Clock },
			{ type: "markRead", label: "Marcar como lida", icon: CircleCheck },
			{ type: "archive", label: "Arquivar", icon: Layers },
			{ type: "block", label: "Bloquear", icon: Ban },
		],
	},
	{
		grupo: "Integrações",
		acento: "integracao",
		itens: [{ type: "integration", label: "Integração", icon: Plug }],
	},
];

const statusVariant = {
	pending: "secondary",
	running: "default",
	waiting: "secondary",
	completed: "secondary",
	failed: "destructive",
};

const statusLabel = {
	pending: "Pendente",
	running: "Executando",
	waiting: "Aguardando resposta",
	completed: "Concluída",
	failed: "Falhou",
};

const FlowBuilder = () => {
	const navigate = useNavigate();
	const { flowId } = useParams();
	const { whatsApps } = useWhatsApps();

	const [flow, setFlow] = useState(null);
	const [name, setName] = useState("");
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const [nodes, setNodes, onNodesChange] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);
	const [selectedNodeId, setSelectedNodeId] = useState(null);
	const [enviandoArquivo, setEnviandoArquivo] = useState(false);
	const arquivoRef = useRef(null);
	const arquivoItemRef = useRef(null);
	const itemDoUpload = useRef(0);

	const [simulatorOpen, setSimulatorOpen] = useState(false);
	const [testDialogOpen, setTestDialogOpen] = useState(false);
	const [testInput, setTestInput] = useState('{\n  "name": "Cliente"\n}');
	const [executionsOpen, setExecutionsOpen] = useState(false);
	const [executions, setExecutions] = useState([]);
	const [loadingExecutions, setLoadingExecutions] = useState(false);

	const [filas, setFilas] = useState([]);
	const [etiquetas, setEtiquetas] = useState([]);
	const [atendentes, setAtendentes] = useState([]);
	const [raias, setRaias] = useState([]);
	const [novaPalavra, setNovaPalavra] = useState("");
	const fileInputRef = useRef(null);

	const handleExportCurrentFlow = () => {
		exportFlowToJson({
			name: name || flow?.name || "Fluxo",
			nodes,
			edges,
		});
	};

	const handleImportIntoEditor = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = async (event) => {
			try {
				const jsonContent = JSON.parse(event.target.result);

				const [resQueues, resUsers, resTags, resWhatsapps, resLanes] = await Promise.all([
					api.get("/queue").catch(() => ({ data: [] })),
					api.get("/users").catch(() => ({ data: [] })),
					api.get("/tags").catch(() => ({ data: [] })),
					api.get("/whatsapp").catch(() => ({ data: [] })),
					api.get("/ticket-lanes").catch(() => ({ data: [] })),
				]);

				const contextData = {
					queues: resQueues.data?.queues || resQueues.data || filas,
					users: resUsers.data?.users || resUsers.data || atendentes,
					tags: resTags.data?.tags || resTags.data || etiquetas,
					whatsapps: resWhatsapps.data || [],
					lanes: resLanes.data?.lanes || resLanes.data || raias,
				};

				const processed = processImportedFlowData(jsonContent, contextData);

				if (processed.nodes && processed.nodes.length > 0) {
					setNodes(processed.nodes.map(n => ({ ...n, deletable: n.type !== "trigger" })));
					setEdges((processed.edges || []).map(e => ({ ...e, type: "deletable" })));
					toast.success("Fluxo carregado no editor com sucesso. Clique em 'Salvar' para gravar as alterações.");
				}
			} catch (err) {
				toast.error(`Erro ao importar fluxo: ${err.message || "Arquivo JSON inválido."}`);
			}
			if (fileInputRef.current) fileInputRef.current.value = "";
		};
		reader.readAsText(file);
	};

	useEffect(() => {
		(async () => {
			setLoading(true);
			try {
				const { data } = await api.get(`/flows/${flowId}`);
				setFlow(data);
				setName(data.name);
				setNodes(
					(data.nodes || []).map((node) => ({
						...node,
						deletable: node.type !== "trigger",
					}))
				);
				setEdges(
					(data.edges || []).map((edge) => ({
						...edge,
						type: "deletable",
					}))
				);
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		})();
	}, [flowId]);

	useEffect(() => {
		(async () => {
			try {
				const [q, t, u, r] = await Promise.all([
					api.get("/queue"),
					api.get("/tags"),
					api.get("/users"),
					api.get("/ticket-lanes").catch(() => ({ data: [] })),
				]);
				setFilas(q.data?.queues || q.data || []);
				setEtiquetas(t.data?.tags || t.data || []);
				setAtendentes(u.data?.users || u.data || []);
				setRaias(r.data?.lanes || r.data || []);
			} catch (err) {
				// Sem as listas os nós ainda abrem; só o seletor fica vazio.
			}
		})();
	}, []);

	const selectedNode = useMemo(
		() => nodes.find((n) => n.id === selectedNodeId) || null,
		[nodes, selectedNodeId]
	);

	const updateSelectedNodeData = (patch) => {
		setNodes((nds) =>
			nds.map((node) =>
				node.id === selectedNodeId
					? { ...node, data: { ...node.data, ...patch } }
					: node
			)
		);
	};

	const palavrasDoNo = useMemo(
		() =>
			Array.isArray(selectedNode?.data?.keywords)
				? selectedNode.data.keywords
				: [],
		[selectedNode]
	);

	const adicionarPalavra = () => {
		const palavra = novaPalavra.trim();

		if (!palavra) return;

		if (
			palavrasDoNo.some((p) => p.toLowerCase() === palavra.toLowerCase())
		) {
			setNovaPalavra("");
			return;
		}

		updateSelectedNodeData({ keywords: [...palavrasDoNo, palavra] });
		setNovaPalavra("");
	};

	const removerPalavra = (palavra) => {
		updateSelectedNodeData({
			keywords: palavrasDoNo.filter((p) => p !== palavra),
		});
	};

	const onConnect = useCallback(
		(params) => setEdges((eds) => addEdge({ ...params, type: "deletable" }, eds)),
		[setEdges]
	);

	const handleAddNode = (type) => {
		const id = `${type}-${Date.now()}`;

		// Empilha o nó novo embaixo do último. A posição antiga espalhava os nós
		// para a direita e eles acabavam fora da tela, atrás do painel lateral.
		const ultimo = nodes[nodes.length - 1];
		const position = ultimo
			? { x: ultimo.position.x, y: ultimo.position.y + 200 }
			: { x: 360, y: 100 };

		// Nasce solto, sem ligação. Ligar sozinho no último nó parecia ajudar,
		// mas o "último" é só o mais recente da lista — quase nunca o lugar onde
		// a pessoa queria continuar. Quem monta o fluxo puxa a linha de onde quer.
		setNodes((nds) => [
			...nds,
			{ id, type, position, data: { ...NODE_DEFAULTS[type] }, deletable: true },
		]);

		setSelectedNodeId(id);
	};

	// Sobe o arquivo para a biblioteca de mídias e guarda o nome dele no nó.
	// Guardamos o nome, não uma URL: o envio lê direto do disco, o que evita
	// fazer o servidor buscar um endereço dele mesmo — exatamente o que o
	// guarda de SSRF barra.
	const enviarArquivo = async (arquivo, aoConcluir) => {
		if (!arquivo) return;

		setEnviandoArquivo(true);

		try {
			const corpo = new FormData();
			corpo.append("medias", arquivo);
			corpo.append("name", arquivo.name);

			const { data } = await api.post("/media-library", corpo, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			aoConcluir(data.mediaUrl, data.mediaType);
			toast.success("Arquivo enviado.");
		} catch (err) {
			toastError(err);
		}

		setEnviandoArquivo(false);
	};

	const handleRemoveSelectedNode = () => {
		if (!selectedNode || selectedNode.type === "trigger") return;
		setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
		setEdges((eds) =>
			eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId)
		);
		setSelectedNodeId(null);
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const cleanNodes = nodes.map(({ id, type, position, data }) => ({
				id,
				type,
				position,
				data,
			}));
			const cleanEdges = edges.map(({ id, source, target, sourceHandle }) => ({
				id,
				source,
				target,
				sourceHandle: sourceHandle || null,
			}));
			const { data } = await api.put(`/flows/${flowId}`, {
				name,
				nodes: cleanNodes,
				edges: cleanEdges,
			});
			setFlow(data);
			toast.success("Fluxo salvo com sucesso.");
		} catch (err) {
			toastError(err);
		}
		setSaving(false);
	};

	const fetchExecutions = async () => {
		setLoadingExecutions(true);
		try {
			const { data } = await api.get(`/flows/${flowId}/executions`);
			setExecutions(data);
		} catch (err) {
			toastError(err);
		}
		setLoadingExecutions(false);
	};

	const handleOpenExecutions = () => {
		setExecutionsOpen(true);
		fetchExecutions();
	};

	const handleRunTest = async () => {
		let parsed = {};
		if (testInput.trim()) {
			try {
				parsed = JSON.parse(testInput);
			} catch (err) {
				toast.error("O payload de teste não é um JSON válido.");
				return;
			}
		}
		try {
			await api.post(`/flows/${flowId}/execute`, { input: parsed });
			toast.success("Execução iniciada.");
			setTestDialogOpen(false);
			handleOpenExecutions();
		} catch (err) {
			toastError(err);
		}
	};

	const webhookUrl =
		flow?.webhookToken && `${getBackendUrl()}/flow-webhooks/${flow.webhookToken}`;

	const handleCopyWebhook = () => {
		navigator.clipboard.writeText(webhookUrl);
		toast.success("URL do webhook copiada.");
	};

	if (loading) {
		return (
			<div className="flex h-full items-center justify-center">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col overflow-hidden">
			{/* Barra superior */}
			<div className="flex shrink-0 items-center gap-3 border-b bg-background px-4 py-2.5">
				<Button
					variant="ghost"
					size="icon"
					onClick={() => navigate("/flows")}
				>
					<ArrowLeft className="h-5 w-5" />
				</Button>
				<Input
					value={name}
					onChange={(e) => setName(e.target.value)}
					className="h-9 max-w-xs font-medium"
				/>
				{flow?.nodes?.some?.(
					(n) => n.type === "trigger" && n.data?.triggerType === "webhook"
				) && (
					<button
						type="button"
						onClick={handleCopyWebhook}
						className="hidden items-center gap-1.5 rounded-full border bg-muted/50 px-3 py-1 text-xs text-muted-foreground hover:bg-muted lg:flex"
						title={webhookUrl}
					>
						<Copy className="h-3 w-3" />
						Copiar URL do webhook
					</button>
				)}
				<input
					type="file"
					ref={fileInputRef}
					accept=".json"
					style={{ display: "none" }}
					onChange={handleImportIntoEditor}
				/>
				<div className="ml-auto flex items-center gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={handleExportCurrentFlow}
						title="Exportar fluxo atual como JSON"
					>
						<Download className="h-4 w-4" />
						Exportar JSON
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => fileInputRef.current?.click()}
						title="Importar fluxo a partir de um JSON"
					>
						<Upload className="h-4 w-4" />
						Importar JSON
					</Button>
					<Button variant="outline" size="sm" onClick={handleOpenExecutions}>
						<History className="h-4 w-4" />
						Execuções
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setSimulatorOpen(true)}
						className="border-emerald-600/30 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-950/30 font-medium"
					>
						<Play className="h-4 w-4 fill-emerald-500/20 text-emerald-600 dark:text-emerald-400" />
						Testar fluxo
					</Button>
					<Button size="sm" onClick={handleSave} disabled={saving}>
						{saving ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Save className="h-4 w-4" />
						)}
						Salvar
					</Button>
				</div>
			</div>

			<div className="flex min-h-0 flex-1">
				{/* Paleta */}
				<div className="scrollbar-discreta w-56 shrink-0 overflow-y-auto border-r bg-muted/30 p-3">
					<p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
						Blocos
					</p>
					{PALETTE.map((secao) => (
						<div key={secao.grupo} className="pb-3">
							<p className="px-1 pb-1.5 text-[11px] font-medium text-muted-foreground">
								{secao.grupo}
							</p>
							<div className="space-y-1">
								{secao.itens.map((item) => (
									<button
										key={item.type}
										type="button"
										onClick={() => handleAddNode(item.type)}
										className="flex w-full items-center gap-2 rounded-lg border border-transparent bg-card px-2 py-1.5 text-left text-[13px] font-medium shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors hover:border-border hover:bg-accent"
									>
										{/* O chip repete a cor que o bloco terá no canvas:
										    a pessoa reconhece antes de ler o nome. */}
										<span
											className={cn(
												"flex h-5 w-5 shrink-0 items-center justify-center rounded-md",
												ACENTOS[secao.acento]
											)}
										>
											<item.icon className="h-3 w-3" />
										</span>
										<span className="truncate">{item.label}</span>
									</button>
								))}
							</div>
						</div>
					))}
				</div>

				{/* Canvas */}
				<div className="flow-typebot min-w-0 flex-1 bg-muted/40">
					<ReactFlow
						nodes={nodes}
						edges={edges}
						onNodesChange={onNodesChange}
						onEdgesChange={onEdgesChange}
						onConnect={onConnect}
						onNodeClick={(_, node) => setSelectedNodeId(node.id)}
						onPaneClick={() => setSelectedNodeId(null)}
						nodeTypes={nodeTypes}
						edgeTypes={edgeTypes}
						fitView
						proOptions={{ hideAttribution: true }}
						// Curva suave em vez do traço reto: é o que faz o desenho
						// parecer um fluxo, e não um diagrama de rede.
						// "deletable" é o nosso edge customizado que mostra lixeira no hover.
						defaultEdgeOptions={{ type: "deletable" }}
						connectionLineType="smoothstep"
					>
						<Background variant="dots" gap={22} size={1.4} className="opacity-60" />
						<Controls showInteractive={false} position="bottom-left" />
						<MiniMap
							pannable
							zoomable
							maskColor="hsl(var(--muted) / 0.6)"
							nodeColor="hsl(var(--muted-foreground) / 0.35)"
							nodeBorderRadius={4}
						/>
					</ReactFlow>
				</div>

				{/* Painel de configuração */}
				{selectedNode && (
					<div className="w-80 shrink-0 space-y-4 overflow-y-auto border-l bg-background p-4">
						<div className="flex items-center justify-between">
							<p className="text-sm font-semibold">
								{NODE_LABELS[selectedNode.type] || selectedNode.type}
							</p>
							{selectedNode.type !== "trigger" && (
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 text-destructive hover:text-destructive"
									onClick={handleRemoveSelectedNode}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							)}
						</div>

						{selectedNode.type === "trigger" && (
							<div className="space-y-1.5">
								<Label>Tipo de gatilho</Label>
								<Select
									value={selectedNode.data.triggerType || "manual"}
									onValueChange={(v) => updateSelectedNodeData({ triggerType: v })}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="manual">Manual</SelectItem>
										<SelectItem value="webhook">Webhook</SelectItem>
										<SelectItem value="keyword">Palavra-chave</SelectItem>
									</SelectContent>
								</Select>
								{selectedNode.data.triggerType === "keyword" ? (
									<p className="text-xs text-muted-foreground">
										O fluxo só começa quando a mensagem do cliente casar com uma
										das palavras. Sem palavra nenhuma, ele não responde.
									</p>
								) : (
									<p className="text-xs text-muted-foreground">
										Ligado a uma conexão, o fluxo roda para toda mensagem que
										chegar nela.
									</p>
								)}
							</div>
						)}

						{selectedNode.type === "typing" && (
							<div className="space-y-1.5">
								<Label htmlFor="typingSeconds">Por quantos segundos</Label>
								<Input
									id="typingSeconds"
									type="number"
									min={1}
									max={300}
									value={selectedNode.data.seconds ?? 3}
									onChange={(e) =>
										updateSelectedNodeData({ seconds: Number(e.target.value) })
									}
								/>
								<p className="text-xs text-muted-foreground">
									O fluxo espera esse tempo antes de seguir — sem a espera o
									aviso e a mensagem chegam juntos e ninguém chega a ver.
								</p>
							</div>
						)}

						{(selectedNode.type === "archive" ||
							selectedNode.type === "block") && (
							<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
								<div className="space-y-0.5">
									<Label htmlFor="desfazer">
										{selectedNode.type === "archive"
											? "Desarquivar em vez de arquivar"
											: "Desbloquear em vez de bloquear"}
									</Label>
									<p className="text-xs text-muted-foreground">
										{selectedNode.type === "block"
											? "Bloquear corta a conversa dos dois lados: o cliente não consegue mais escrever."
											: "Arquivar tira a conversa da lista sem encerrar o atendimento."}
									</p>
								</div>
								<Switch
									id="desfazer"
									checked={Boolean(selectedNode.data.desfazer)}
									onCheckedChange={(v) =>
										updateSelectedNodeData({ desfazer: v })
									}
								/>
							</div>
						)}

						{selectedNode.type === "board" && (
							<div className="space-y-1.5">
								<Label>Para qual raia</Label>
								<Select
									value={
										selectedNode.data.laneId
											? String(selectedNode.data.laneId)
											: undefined
									}
									onValueChange={(v) => {
										const raia = raias.find((r) => String(r.id) === v);
										updateSelectedNodeData({
											laneId: v,
											laneName: raia?.name || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Selecione a raia" />
									</SelectTrigger>
									<SelectContent>
										{raias.map((raia) => (
											<SelectItem key={raia.id} value={String(raia.id)}>
												{raia.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									Move o atendimento no quadro de acompanhamento da equipe.
								</p>
							</div>
						)}

						{selectedNode.type === "trigger" && (
							<>
								<div className="space-y-1.5">
									<Label>Onde o fluxo vale</Label>
									<Select
										value={selectedNode.data.escopo || "individual"}
										onValueChange={(v) => updateSelectedNodeData({ escopo: v })}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="individual">
												Conversa individual
											</SelectItem>
											<SelectItem value="grupo">Somente em grupos</SelectItem>
											<SelectItem value="ambos">
												Conversas individuais e grupos
											</SelectItem>
										</SelectContent>
									</Select>
									{selectedNode.data.escopo === "grupo" ||
									selectedNode.data.escopo === "ambos" ? (
										<p className="text-xs text-amber-600 dark:text-amber-500">
											Em grupo, tudo o que o robô disser aparece para todos os
											participantes. A conexão também precisa estar com "grupos
											viram atendimento" ligado.
										</p>
									) : (
										<p className="text-xs text-muted-foreground">
											O robô não fala em grupos.
										</p>
									)}
								</div>

								{(selectedNode.data.escopo === "grupo" ||
									selectedNode.data.escopo === "ambos") && (
									<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
										<div className="space-y-0.5">
											<Label htmlFor="triggerPrivada">
												Responder no privado
											</Label>
											<p className="text-xs text-muted-foreground">
												Veio de grupo, o robô chama quem escreveu na conversa
												individual em vez de falar no grupo.
											</p>
										</div>
										<Switch
											id="triggerPrivada"
											checked={Boolean(selectedNode.data.respostaPrivada)}
											onCheckedChange={(v) =>
												updateSelectedNodeData({ respostaPrivada: v })
											}
										/>
									</div>
								)}
							</>
						)}

						{selectedNode.type === "trigger" &&
							selectedNode.data.triggerType === "keyword" && (
								<>
									<div className="space-y-1.5">
										<Label htmlFor="triggerKeyword">Palavras-chave</Label>
										<div className="flex gap-2">
											<Input
												id="triggerKeyword"
												value={novaPalavra}
												placeholder="Ex.: orçamento"
												onChange={(e) => setNovaPalavra(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														e.preventDefault();
														adicionarPalavra();
													}
												}}
											/>
											<Button
												type="button"
												variant="secondary"
												onClick={adicionarPalavra}
											>
												<Plus className="h-4 w-4" />
											</Button>
										</div>
										{palavrasDoNo.length > 0 && (
											<div className="flex flex-wrap gap-1.5 pt-1">
												{palavrasDoNo.map((palavra) => (
													<Badge
														key={palavra}
														variant="secondary"
														className="gap-1"
													>
														{palavra}
														<button
															type="button"
															aria-label={`Remover ${palavra}`}
															onClick={() => removerPalavra(palavra)}
															className="text-muted-foreground hover:text-foreground"
														>
															<Trash className="h-3 w-3" />
														</button>
													</Badge>
												))}
											</div>
										)}
									</div>

									<div className="space-y-1.5">
										<Label>Como comparar</Label>
										<Select
											value={selectedNode.data.matchMode || "contains"}
											onValueChange={(v) =>
												updateSelectedNodeData({ matchMode: v })
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="contains">
													Contém a palavra
												</SelectItem>
												<SelectItem value="startsWith">
													Começa com a palavra
												</SelectItem>
												<SelectItem value="wholeWord">
													Palavra inteira
												</SelectItem>
												<SelectItem value="exact">
													Mensagem exatamente igual
												</SelectItem>
											</SelectContent>
										</Select>
										<p className="text-xs text-muted-foreground">
											Acento é ignorado sempre: "orcamento" encontra
											"orçamento".
										</p>
									</div>

									<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
										<div className="space-y-0.5">
											<Label htmlFor="triggerCase">
												Diferenciar maiúsculas
											</Label>
											<p className="text-xs text-muted-foreground">
												Com isto ligado, "Orçamento" e "orçamento" são
												palavras diferentes.
											</p>
										</div>
										<Switch
											id="triggerCase"
											checked={Boolean(selectedNode.data.caseSensitive)}
											onCheckedChange={(v) =>
												updateSelectedNodeData({ caseSensitive: v })
											}
										/>
									</div>

									<div className="space-y-1.5">
										<Label>Esperar antes de responder (segundos)</Label>
										<div className="flex items-center gap-2">
											<Input
												type="number"
												min={0}
												max={300}
												aria-label="Espera mínima em segundos"
												value={selectedNode.data.delayMin ?? 0}
												onChange={(e) =>
													updateSelectedNodeData({
														delayMin: Number(e.target.value),
													})
												}
											/>
											<span className="text-sm text-muted-foreground">a</span>
											<Input
												type="number"
												min={0}
												max={300}
												aria-label="Espera máxima em segundos"
												value={selectedNode.data.delayMax ?? 0}
												onChange={(e) =>
													updateSelectedNodeData({
														delayMax: Number(e.target.value),
													})
												}
											/>
										</div>
										<p className="text-xs text-muted-foreground">
											Sorteia um tempo entre os dois. Zero nos dois responde na
											hora. Máximo de 300 segundos.
										</p>
									</div>

									<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
										<div className="space-y-0.5">
											<Label htmlFor="triggerAtendida">
												Não responder se já tem atendente
											</Label>
											<p className="text-xs text-muted-foreground">
												Quando alguém da equipe já assumiu a conversa, o robô
												fica quieto em vez de falar por cima.
											</p>
										</div>
										<Switch
											id="triggerAtendida"
											checked={Boolean(selectedNode.data.naoSeAtendida)}
											onCheckedChange={(v) =>
												updateSelectedNodeData({ naoSeAtendida: v })
											}
										/>
									</div>

									<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
										<div className="space-y-0.5">
											<Label htmlFor="triggerMidia">
												Só quando vier arquivo
											</Label>
											<p className="text-xs text-muted-foreground">
												Responde apenas se a mensagem trouxe foto, áudio,
												vídeo ou documento.
											</p>
										</div>
										<Switch
											id="triggerMidia"
											checked={Boolean(selectedNode.data.somenteComMidia)}
											onCheckedChange={(v) =>
												updateSelectedNodeData({ somenteComMidia: v })
											}
										/>
									</div>
								</>
							)}

						{selectedNode.type === "sendMessage" && (
							<>
								<div className="space-y-1.5">
									<Label>Conexão</Label>
									<Select
										value={
											selectedNode.data.whatsappId
												? String(selectedNode.data.whatsappId)
												: undefined
										}
										onValueChange={(v) =>
											updateSelectedNodeData({ whatsappId: v })
										}
									>
										<SelectTrigger>
											<SelectValue placeholder="Selecione a conexão" />
										</SelectTrigger>
										<SelectContent>
											{whatsApps.map((w) => (
												<SelectItem key={w.id} value={String(w.id)}>
													{w.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="nodeNumber">Número</Label>
									<Input
										id="nodeNumber"
										value={selectedNode.data.number || ""}
										onChange={(e) =>
											updateSelectedNodeData({ number: e.target.value })
										}
										placeholder="5511999999999"
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="nodeBody">Mensagem</Label>
									<Textarea
										id="nodeBody"
										rows={4}
										value={selectedNode.data.body || ""}
										onChange={(e) =>
											updateSelectedNodeData({ body: e.target.value })
										}
										placeholder="Olá {{name}}!"
									/>
									<p className="text-xs text-muted-foreground">
										Use {"{{name}}"} para inserir o nome vindo do gatilho.
									</p>
								</div>
							</>
						)}

						{selectedNode.type === "delay" && (
							<div className="space-y-1.5">
								<Label htmlFor="nodeSeconds">Segundos</Label>
								<Input
									id="nodeSeconds"
									type="number"
									min={0}
									max={300}
									value={selectedNode.data.seconds ?? 0}
									onChange={(e) =>
										updateSelectedNodeData({ seconds: Number(e.target.value) })
									}
								/>
								<p className="text-xs text-muted-foreground">Máximo de 300s.</p>
							</div>
						)}

						{selectedNode.type === "followUp" && (
							<div className="space-y-4">
								<div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50/50 p-3 text-xs leading-relaxed text-fuchsia-900 dark:border-fuchsia-500/20 dark:bg-fuchsia-950/20 dark:text-fuchsia-300">
									<p className="font-medium">⚡ Automação de Acompanhamento</p>
									<p className="mt-1 text-[11px] opacity-90">
										Envia mensagens automaticamente em intervalos configurados caso o cliente não responda. Se o cliente responder a qualquer momento, o Follow-up é cancelado e segue pela saída <strong>"Cliente respondeu"</strong>.
									</p>
								</div>

								<div className="space-y-3">
									<div className="flex items-center justify-between">
										<Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
											Etapas de Follow-up ({(selectedNode.data.steps || []).length})
										</Label>
										<Button
											type="button"
											variant="outline"
											size="sm"
											className="h-7 gap-1 text-xs text-primary"
											onClick={() => {
												const steps = [...(selectedNode.data.steps || [])];
												steps.push({
													delay: 1,
													unit: steps.length === 0 ? "hours" : steps[steps.length - 1].unit || "days",
													message: "",
												});
												updateSelectedNodeData({ steps });
											}}
										>
											<Plus className="h-3.5 w-3.5" />
											Adicionar etapa
										</Button>
									</div>

									{(selectedNode.data.steps || []).map((step, idx) => (
										<div
											key={idx}
											className="space-y-2 rounded-lg border bg-card p-3 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
										>
											<div className="flex items-center justify-between">
												<span className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
													<span className="flex h-5 w-5 items-center justify-center rounded-full bg-fuchsia-100 text-[10px] font-bold text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300">
														{idx + 1}
													</span>
													Etapa {idx + 1}
												</span>
												{(selectedNode.data.steps || []).length > 1 && (
													<Button
														type="button"
														variant="ghost"
														size="icon"
														className="h-7 w-7 text-destructive hover:text-destructive"
														title="Remover etapa"
														onClick={() => {
															const steps = (selectedNode.data.steps || []).filter(
																(_, i) => i !== idx
															);
															updateSelectedNodeData({ steps });
														}}
													>
														<Trash2 className="h-3.5 w-3.5" />
													</Button>
												)}
											</div>

											<div className="space-y-1">
												<Label className="text-[11px] text-muted-foreground">
													Aguardar sem resposta por:
												</Label>
												<div className="flex items-center gap-2">
													<Input
														type="number"
														min={1}
														className="w-20"
														value={step.delay ?? 1}
														onChange={(e) => {
															const steps = [...(selectedNode.data.steps || [])];
															steps[idx] = {
																...steps[idx],
																delay: Math.max(1, Number(e.target.value) || 1),
															};
															updateSelectedNodeData({ steps });
														}}
													/>
													<Select
														value={step.unit || "hours"}
														onValueChange={(val) => {
															const steps = [...(selectedNode.data.steps || [])];
															steps[idx] = { ...steps[idx], unit: val };
															updateSelectedNodeData({ steps });
														}}
													>
														<SelectTrigger className="flex-1">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="minutes">Minuto(s)</SelectItem>
															<SelectItem value="hours">Hora(s)</SelectItem>
															<SelectItem value="days">Dia(s)</SelectItem>
														</SelectContent>
													</Select>
												</div>
											</div>

											<div className="space-y-1">
												<Label className="text-[11px] text-muted-foreground">
													Mensagem da etapa:
												</Label>
												<Textarea
													rows={3}
													className="text-xs"
													value={step.message || ""}
													placeholder="Ex.: Olá {{nome}}! Conseguiu verificar nossa proposta?"
													onChange={(e) => {
														const steps = [...(selectedNode.data.steps || [])];
														steps[idx] = { ...steps[idx], message: e.target.value };
														updateSelectedNodeData({ steps });
													}}
												/>
											</div>
										</div>
									))}

									{(selectedNode.data.steps || []).length === 0 && (
										<div className="rounded-lg border border-dashed p-4 text-center">
											<p className="text-xs text-muted-foreground">
												Nenhuma etapa adicionada.
											</p>
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="mt-2 gap-1 text-xs"
												onClick={() => {
													updateSelectedNodeData({
														steps: [
															{ delay: 1, unit: "hours", message: "" }
														],
													});
												}}
											>
												<Plus className="h-3.5 w-3.5" /> Adicionar 1ª Etapa
											</Button>
										</div>
									)}
								</div>

								<div className="space-y-2 rounded-lg border bg-muted/30 p-3">
									<div className="flex items-center justify-between">
										<div className="space-y-0.5">
											<Label className="text-xs font-medium">Encerrar atendimento ao concluir</Label>
											<p className="text-[11px] text-muted-foreground">
												Se todas as etapas forem enviadas sem resposta do cliente, fecha o ticket automaticamente.
											</p>
										</div>
										<Switch
											checked={Boolean(selectedNode.data.closeOnComplete)}
											onCheckedChange={(checked) =>
												updateSelectedNodeData({ closeOnComplete: checked })
											}
										/>
									</div>
								</div>

								<div className="rounded-lg bg-muted/40 p-2.5 text-[11px] text-muted-foreground">
									<p className="font-medium text-foreground">💡 Dica de variáveis</p>
									<p className="mt-0.5">
										Você pode usar <code className="rounded bg-muted px-1 py-0.5 text-[10px]">&#123;&#123;nome&#125;&#125;</code>, <code className="rounded bg-muted px-1 py-0.5 text-[10px]">&#123;&#123;numero&#125;&#125;</code> e variáveis coletadas anteriormente.
									</p>
								</div>
							</div>
						)}

						{selectedNode.type === "createDeal" && (
							<div className="space-y-1.5">
								<Label htmlFor="nodeDealTitle">Título da Oportunidade (CRM)</Label>
								<Input
									id="nodeDealTitle"
									value={selectedNode.data.title || ""}
									onChange={(e) =>
										updateSelectedNodeData({ title: e.target.value })
									}
									placeholder="Ex.: {{nome}} - Oportunidade"
								/>
								<p className="text-xs text-muted-foreground">
									Aceita variáveis como &#123;&#123;nome&#125;&#125; e &#123;&#123;numero&#125;&#125;.
								</p>
							</div>
						)}

						{selectedNode.type === "condition" && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="nodeField">Campo</Label>
									<Input
										id="nodeField"
										value={selectedNode.data.field || ""}
										onChange={(e) =>
											updateSelectedNodeData({ field: e.target.value })
										}
										placeholder="ex.: message.body"
									/>
								</div>
								<div className="space-y-1.5">
									<Label>Operador</Label>
									<Select
										value={selectedNode.data.operator || "equals"}
										onValueChange={(v) => updateSelectedNodeData({ operator: v })}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="equals">É igual a</SelectItem>
											<SelectItem value="contains">Contém</SelectItem>
											<SelectItem value="exists">Existe</SelectItem>
										</SelectContent>
									</Select>
								</div>
								{selectedNode.data.operator !== "exists" && (
									<div className="space-y-1.5">
										<Label htmlFor="nodeValue">Valor</Label>
										<Input
											id="nodeValue"
											value={selectedNode.data.value || ""}
											onChange={(e) =>
												updateSelectedNodeData({ value: e.target.value })
											}
										/>
									</div>
								)}
							</>
						)}

						{selectedNode.type === "addTag" && (
							<div className="space-y-1.5">
								<Label>Etiqueta</Label>
								<Select
									value={String(selectedNode.data.tagId || "")}
									onValueChange={(v) => {
										const alvo = etiquetas.find((t) => String(t.id) === v);
										updateSelectedNodeData({
											tagId: v,
											// O nome fica junto para o nó no canvas mostrar algo
											// legível sem depender de outra consulta.
											tagName: alvo?.name || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Escolha a etiqueta" />
									</SelectTrigger>
									<SelectContent>
										{etiquetas.map((t) => (
											<SelectItem key={t.id} value={String(t.id)}>
												{t.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									Só funciona em fluxo disparado por um atendimento.
								</p>
							</div>
						)}

						{selectedNode.type === "transferQueue" && (
							<div className="space-y-1.5">
								<Label>Fila</Label>
								<Select
									value={String(selectedNode.data.queueId || "")}
									onValueChange={(v) => {
										const alvo = filas.find((f) => String(f.id) === v);
										updateSelectedNodeData({
											queueId: v,
											queueName: alvo?.name || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Escolha a fila" />
									</SelectTrigger>
									<SelectContent>
										{filas.map((f) => (
											<SelectItem key={f.id} value={String(f.id)}>
												{f.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									O atendimento volta para "aguardando" na nova fila.
								</p>
							</div>
						)}

						{selectedNode.type === "note" && (
							<div className="space-y-1.5">
								<Label htmlFor="nodeNote">Texto da nota</Label>
								<Textarea
									id="nodeNote"
									rows={4}
									value={selectedNode.data.note || ""}
									onChange={(e) =>
										updateSelectedNodeData({ note: e.target.value })
									}
									placeholder="Cliente respondeu pela automação."
								/>
								<p className="text-xs text-muted-foreground">
									Fica visível só para a equipe, nunca para o cliente.
								</p>
							</div>
						)}

						{selectedNode.type === "ai" && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="nodePrompt">Instrução para a IA (prompt)</Label>
									<Textarea
										id="nodePrompt"
										rows={5}
										value={selectedNode.data.prompt || ""}
										onChange={(e) =>
											updateSelectedNodeData({ prompt: e.target.value })
										}
										placeholder="Olá, crie uma saudação descontraída e amigável para {{nome}}, perguntando se gostaria de falar com Financeiro, Comercial ou Comprar, e avise que vai enviar as opções a seguir."
									/>
									<p className="text-xs text-muted-foreground">
										A IA vai gerar a mensagem natural usando essas instruções.
									</p>
								</div>

								<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
									<div className="space-y-0.5">
										<Label htmlFor="aiSendToCustomer">Enviar fala gerada ao cliente</Label>
										<p className="text-xs text-muted-foreground">
											Envia a resposta da IA no WhatsApp antes de passar para o próximo bloco (ex: Botões).
										</p>
									</div>
									<Switch
										id="aiSendToCustomer"
										checked={selectedNode.data.sendToCustomer !== false}
										onCheckedChange={(v) =>
											updateSelectedNodeData({ sendToCustomer: v })
										}
									/>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="aiGreeting">Saudação inicial</Label>
									<Textarea
										id="aiGreeting"
										rows={3}
										value={selectedNode.data.greeting || ""}
										onChange={(e) =>
											updateSelectedNodeData({ greeting: e.target.value })
										}
										placeholder="Olá {{nome}}! 😊 Como posso te ajudar hoje?"
									/>
									<p className="text-xs text-muted-foreground">
										Primeira mensagem que o cliente recebe. Use {"{{nome}}"} para personalizar.
									</p>
								</div>

								<div className="space-y-2">
									<Label>Gatilhos (opções que a IA pode escolher)</Label>
									{(selectedNode.data.options || []).map((opcao, i) => (
										<div key={i} className="space-y-1.5 rounded-lg border p-2">
											<div className="flex items-center gap-1.5">
												<Input
													className="w-14 shrink-0 text-center"
													value={opcao.number}
													aria-label={`Número do gatilho ${i + 1}`}
													onChange={(e) => {
														const opcoes = [...selectedNode.data.options];
														opcoes[i] = { ...opcoes[i], number: e.target.value };
														updateSelectedNodeData({ options: opcoes });
													}}
												/>
												<Input
													value={opcao.label}
													aria-label={`Texto do gatilho ${i + 1}`}
													placeholder="Vendas"
													onChange={(e) => {
														const opcoes = [...selectedNode.data.options];
														opcoes[i] = { ...opcoes[i], label: e.target.value };
														updateSelectedNodeData({ options: opcoes });
													}}
												/>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
													aria-label={`Remover gatilho ${i + 1}`}
													onClick={() => {
														const alvo = `opt-${opcao.number}`;
														setEdges((eds) =>
															eds.filter(
																(e) =>
																	!(
																		e.source === selectedNode.id &&
																		e.sourceHandle === alvo
																	)
															)
														);
														updateSelectedNodeData({
															options: selectedNode.data.options.filter(
																(_, idx) => idx !== i
															),
														});
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
											<Input
												className="text-xs"
												value={opcao.description || ""}
												aria-label={`Descrição do gatilho ${i + 1}`}
												placeholder="Descrição: ex. quer saber preços e planos"
												onChange={(e) => {
													const opcoes = [...selectedNode.data.options];
													opcoes[i] = { ...opcoes[i], description: e.target.value };
													updateSelectedNodeData({ options: opcoes });
												}}
											/>
										</div>
									))}
									<Button
										variant="outline"
										size="sm"
										className="w-full"
										onClick={() => {
											const opcoes = selectedNode.data.options || [];
											updateSelectedNodeData({
												options: [
													...opcoes,
													{ number: String(opcoes.length + 1), label: "", description: "" },
												],
											});
										}}
									>
										<Plus className="h-4 w-4" />
										Adicionar gatilho
									</Button>
									<p className="text-xs text-muted-foreground">
										A IA conversa naturalmente e, ao identificar a intenção do
										cliente, seleciona o gatilho. Cada gatilho cria uma saída no
										bloco que você pode ligar a outros nós (transferir fila,
										etiqueta, etc.).
									</p>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="aiMaxTurns">Máximo de turnos de conversa</Label>
									<Input
										id="aiMaxTurns"
										type="number"
										min={1}
										max={20}
										value={selectedNode.data.maxTurns ?? 5}
										onChange={(e) =>
											updateSelectedNodeData({ maxTurns: Number(e.target.value) })
										}
									/>
									<p className="text-xs text-muted-foreground">
										Depois disso, se a IA não classificou, segue pela saída
										"Se atingir limite". Padrão: 5.
									</p>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="aiTimeoutMsg">Mensagem quando atingir o limite</Label>
									<Textarea
										id="aiTimeoutMsg"
										rows={2}
										value={selectedNode.data.timeoutMessage || ""}
										onChange={(e) =>
											updateSelectedNodeData({ timeoutMessage: e.target.value })
										}
										placeholder="Desculpe, não consegui identificar. Vou te encaminhar..."
									/>
								</div>

								<p className="text-xs text-muted-foreground">
									Sem gatilhos, funciona como antes: a IA processa e a resposta
									fica em {"{{ai}}"} para os nós seguintes.
								</p>
							</>
						)}

						{selectedNode.type === "content" && (
							<div className="space-y-2">
								<Label>Mensagens, na ordem</Label>
								{(selectedNode.data.items || []).map((item, i) => (
									<div key={i} className="space-y-1.5 rounded-lg border p-2">
										<div className="flex items-center gap-1.5">
											<Select
												value={item.kind}
												onValueChange={(v) => {
													const itens = [...selectedNode.data.items];
													itens[i] = { ...itens[i], kind: v };
													updateSelectedNodeData({ items: itens });
												}}
											>
												<SelectTrigger className="h-8 flex-1 text-xs">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="text">Texto</SelectItem>
													<SelectItem value="image">Imagem</SelectItem>
													<SelectItem value="audio">Áudio</SelectItem>
													<SelectItem value="video">Vídeo</SelectItem>
													<SelectItem value="document">Documento</SelectItem>
													<SelectItem value="delay">Espera</SelectItem>
												</SelectContent>
											</Select>
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
												aria-label={`Remover item ${i + 1}`}
												onClick={() =>
													updateSelectedNodeData({
														items: selectedNode.data.items.filter(
															(_, idx) => idx !== i
														),
													})
												}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>

										{item.kind === "delay" ? (
											<Input
												type="number"
												min={0}
												className="h-8 text-xs"
												aria-label={`Segundos do item ${i + 1}`}
												value={item.seconds ?? 2}
												onChange={(e) => {
													const itens = [...selectedNode.data.items];
													itens[i] = {
														...itens[i],
														seconds: Number(e.target.value),
													};
													updateSelectedNodeData({ items: itens });
												}}
											/>
										) : item.kind === "text" ? (
											<Textarea
												rows={2}
												className="text-xs"
												aria-label={`Texto do item ${i + 1}`}
												placeholder="Olá {{name}}!"
												value={item.value || ""}
												onChange={(e) => {
													const itens = [...selectedNode.data.items];
													itens[i] = { ...itens[i], value: e.target.value };
													updateSelectedNodeData({ items: itens });
												}}
											/>
										) : (
											<>
												<div className="flex gap-1.5">
													<Input
														className="h-8 text-xs"
														aria-label={`Arquivo do item ${i + 1}`}
														placeholder="Escolha um arquivo ou cole um endereço"
														value={item.value || ""}
														onChange={(e) => {
															const itens = [...selectedNode.data.items];
															itens[i] = { ...itens[i], value: e.target.value };
															updateSelectedNodeData({ items: itens });
														}}
													/>
													<Button
														type="button"
														variant="outline"
														size="icon"
														className="h-8 w-8 shrink-0"
														disabled={enviandoArquivo}
														aria-label={`Enviar arquivo do item ${i + 1}`}
														onClick={() => {
															itemDoUpload.current = i;
															arquivoItemRef.current?.click();
														}}
													>
														{enviandoArquivo ? (
															<Loader2 className="h-3.5 w-3.5 animate-spin" />
														) : (
															<Upload className="h-3.5 w-3.5" />
														)}
													</Button>
												</div>
												<Input
													className="h-8 text-xs"
													aria-label={`Legenda do item ${i + 1}`}
													placeholder="Legenda (opcional)"
													value={item.caption || ""}
													onChange={(e) => {
														const itens = [...selectedNode.data.items];
														itens[i] = { ...itens[i], caption: e.target.value };
														updateSelectedNodeData({ items: itens });
													}}
												/>
											</>
										)}
									</div>
								))}

								{/* Um input só para a lista inteira: qual item recebe o
								    arquivo fica em itemDoUpload. */}
								<input
									ref={arquivoItemRef}
									type="file"
									className="hidden"
									onChange={(e) => {
										const alvo = itemDoUpload.current;
										enviarArquivo(e.target.files?.[0], (nome, tipo) => {
											const itens = [...selectedNode.data.items];
											itens[alvo] = {
												...itens[alvo],
												value: nome,
												kind: tipo || itens[alvo].kind,
											};
											updateSelectedNodeData({ items: itens });
										});
										e.target.value = "";
									}}
								/>

								<Button
									variant="outline"
									size="sm"
									className="w-full"
									onClick={() =>
										updateSelectedNodeData({
											items: [
												...(selectedNode.data.items || []),
												{ kind: "text", value: "" },
											],
										})
									}
								>
									<Plus className="h-4 w-4" />
									Adicionar mensagem
								</Button>
								<p className="text-xs text-muted-foreground">
									Saem uma após a outra, nesta ordem.
								</p>
							</div>
						)}

						{selectedNode.type === "integration" && (
							<>
								<div className="space-y-1.5">
									<Label>Serviço</Label>
									<Select
										value={selectedNode.data.service || "http"}
										onValueChange={(v) => updateSelectedNodeData({ service: v })}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="http">Requisição HTTP</SelectItem>
											<SelectItem value="sheets">Google Sheets</SelectItem>
											<SelectItem value="asaas">Asaas (cobrança)</SelectItem>
										</SelectContent>
									</Select>
								</div>

								{(selectedNode.data.service || "http") === "http" && (
									<>
										<div className="space-y-1.5">
											<Label>Método</Label>
											<Select
												value={selectedNode.data.method || "GET"}
												onValueChange={(v) => updateSelectedNodeData({ method: v })}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="GET">GET</SelectItem>
													<SelectItem value="POST">POST</SelectItem>
													<SelectItem value="PUT">PUT</SelectItem>
													<SelectItem value="PATCH">PATCH</SelectItem>
													<SelectItem value="DELETE">DELETE</SelectItem>
												</SelectContent>
											</Select>
										</div>
										<div className="space-y-1.5">
											<Label htmlFor="intUrl">URL</Label>
											<Input
												id="intUrl"
												value={selectedNode.data.url || ""}
												onChange={(e) => updateSelectedNodeData({ url: e.target.value })}
												placeholder="https://api.exemplo.com/clientes"
											/>
										</div>

										<div className="space-y-2">
											<Label>Cabeçalhos</Label>
											{(selectedNode.data.headers || []).map((h, i) => (
												<div key={i} className="flex items-center gap-1.5">
													<Input
														className="h-8 text-xs"
														aria-label={`Nome do cabeçalho ${i + 1}`}
														placeholder="Authorization"
														value={h.key || ""}
														onChange={(e) => {
															const hs = [...selectedNode.data.headers];
															hs[i] = { ...hs[i], key: e.target.value };
															updateSelectedNodeData({ headers: hs });
														}}
													/>
													<Input
														className="h-8 text-xs"
														aria-label={`Valor do cabeçalho ${i + 1}`}
														placeholder="Bearer ..."
														value={h.value || ""}
														onChange={(e) => {
															const hs = [...selectedNode.data.headers];
															hs[i] = { ...hs[i], value: e.target.value };
															updateSelectedNodeData({ headers: hs });
														}}
													/>
													<Button
														variant="ghost"
														size="icon"
														className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
														aria-label={`Remover cabeçalho ${i + 1}`}
														onClick={() =>
															updateSelectedNodeData({
																headers: selectedNode.data.headers.filter(
																	(_, idx) => idx !== i
																),
															})
														}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											))}
											<Button
												variant="outline"
												size="sm"
												className="w-full"
												onClick={() =>
													updateSelectedNodeData({
														headers: [
															...(selectedNode.data.headers || []),
															{ key: "", value: "" },
														],
													})
												}
											>
												<Plus className="h-4 w-4" />
												Adicionar cabeçalho
											</Button>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="intBody">Corpo</Label>
											<Textarea
												id="intBody"
												rows={4}
												value={selectedNode.data.body || ""}
												onChange={(e) => updateSelectedNodeData({ body: e.target.value })}
												placeholder='{"nome": "{{name}}"}'
											/>
											<p className="text-xs text-muted-foreground">
												A resposta fica em {"{{http.data}}"} e {"{{http.status}}"}.
											</p>
										</div>
									</>
								)}

								{selectedNode.data.service === "sheets" && (
									<>
										<div className="space-y-1.5">
											<Label>O que fazer</Label>
											<Select
												value={selectedNode.data.operation || "append"}
												onValueChange={(v) => updateSelectedNodeData({ operation: v })}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="append">Gravar uma linha</SelectItem>
													<SelectItem value="read">Ler um intervalo</SelectItem>
												</SelectContent>
											</Select>
										</div>
										<div className="space-y-1.5">
											<Label htmlFor="sheetId">ID da planilha</Label>
											<Input
												id="sheetId"
												value={selectedNode.data.spreadsheetId || ""}
												onChange={(e) =>
													updateSelectedNodeData({ spreadsheetId: e.target.value })
												}
												placeholder="1BxiMVs0XRA5nFMdKvBd..."
											/>
											<p className="text-xs text-muted-foreground">
												É o trecho da URL entre /d/ e /edit.
											</p>
										</div>
										<div className="space-y-1.5">
											<Label htmlFor="sheetRange">Intervalo</Label>
											<Input
												id="sheetRange"
												value={selectedNode.data.range || ""}
												onChange={(e) => updateSelectedNodeData({ range: e.target.value })}
												placeholder="Página1!A:D"
											/>
										</div>
										{selectedNode.data.operation === "append" && (
											<div className="space-y-1.5">
												<Label htmlFor="sheetValues">Valores da linha</Label>
												<Input
													id="sheetValues"
													value={selectedNode.data.values || ""}
													onChange={(e) =>
														updateSelectedNodeData({ values: e.target.value })
													}
													placeholder="{{name}};{{number}};{{email}}"
												/>
												<p className="text-xs text-muted-foreground">
													Uma coluna por valor, separados por ponto e vírgula.
												</p>
											</div>
										)}
										<div className="space-y-1.5">
											<Label htmlFor="sheetToken">Token de acesso</Label>
											<Input
												id="sheetToken"
												type="password"
												value={selectedNode.data.token || ""}
												onChange={(e) => updateSelectedNodeData({ token: e.target.value })}
											/>
											<p className="text-xs text-muted-foreground">
												Token OAuth do Google com permissão na planilha.
											</p>
										</div>
									</>
								)}

								{selectedNode.data.service === "asaas" && (
									<>
										<div className="space-y-1.5">
											<Label>O que fazer</Label>
											<Select
												value={selectedNode.data.operation || "create"}
												onValueChange={(v) => updateSelectedNodeData({ operation: v })}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="create">Criar cobrança</SelectItem>
													<SelectItem value="status">Consultar cobrança</SelectItem>
												</SelectContent>
											</Select>
										</div>

										{selectedNode.data.operation === "status" ? (
											<div className="space-y-1.5">
												<Label htmlFor="chargeId">ID da cobrança</Label>
												<Input
													id="chargeId"
													value={selectedNode.data.chargeId || ""}
													onChange={(e) =>
														updateSelectedNodeData({ chargeId: e.target.value })
													}
													placeholder="{{asaas.id}}"
												/>
											</div>
										) : (
											<>
												<div className="space-y-1.5">
													<Label htmlFor="chargeValue">Valor (R$)</Label>
													<Input
														id="chargeValue"
														value={selectedNode.data.value || ""}
														onChange={(e) =>
															updateSelectedNodeData({ value: e.target.value })
														}
														placeholder="99.90"
													/>
												</div>
												{/* O ExecuteFlowService lê data.dueDate e o editor
												    nunca gravava: toda cobrança saía com o padrão de
												    3 dias, sem jeito de mudar. */}
												<div className="space-y-1.5">
													<Label htmlFor="chargeDueDate">Vence em</Label>
													<Input
														id="chargeDueDate"
														type="date"
														value={selectedNode.data.dueDate || ""}
														onChange={(e) =>
															updateSelectedNodeData({ dueDate: e.target.value })
														}
													/>
													<p className="text-xs text-muted-foreground">
														Em branco, vence em 3 dias. Cobrança que vence no
														mesmo dia chega junto com o susto.
													</p>
												</div>
												<div className="space-y-1.5">
													<Label>Forma de pagamento</Label>
													<Select
														value={selectedNode.data.billingType || "PIX"}
														onValueChange={(v) =>
															updateSelectedNodeData({ billingType: v })
														}
													>
														<SelectTrigger>
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="PIX">PIX</SelectItem>
															<SelectItem value="BOLETO">Boleto</SelectItem>
															<SelectItem value="CREDIT_CARD">Cartão</SelectItem>
														</SelectContent>
													</Select>
												</div>
												<div className="space-y-1.5">
													<Label htmlFor="chargeDesc">Descrição</Label>
													<Input
														id="chargeDesc"
														value={selectedNode.data.description || ""}
														onChange={(e) =>
															updateSelectedNodeData({ description: e.target.value })
														}
													/>
												</div>
											</>
										)}
										<p className="text-xs text-muted-foreground">
											A cobrança sai em {"{{asaas.link}}"}, e {"{{asaas.pago}}"} diz
											se já foi paga. Usa a chave do Asaas das configurações.
										</p>
									</>
								)}
							</>
						)}

						{["menu", "buttons", "list"].includes(selectedNode.type) && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="menuMessage">Pergunta</Label>
									<Textarea
										id="menuMessage"
										rows={3}
										value={selectedNode.data.message || ""}
										onChange={(e) =>
											updateSelectedNodeData({ message: e.target.value })
										}
										placeholder="Como podemos ajudar?"
									/>
									<p className="text-xs text-muted-foreground">
										As opções abaixo são listadas automaticamente depois dela.
									</p>
								</div>

								<div className="space-y-2">
									<Label>Opções</Label>
									{(selectedNode.data.options || []).map((opcao, i) => (
										<div key={i} className="space-y-1.5">
											<div className="flex items-center gap-1.5">
												<Input
													className="w-14 shrink-0 text-center"
													value={opcao.number}
													aria-label={`Número da opção ${i + 1}`}
													onChange={(e) => {
														const opcoes = [...selectedNode.data.options];
														opcoes[i] = { ...opcoes[i], number: e.target.value };
														updateSelectedNodeData({ options: opcoes });
													}}
												/>
												<Input
													value={opcao.label}
													aria-label={`Texto da opção ${i + 1}`}
													placeholder="Falar com vendas"
													onChange={(e) => {
														const opcoes = [...selectedNode.data.options];
														opcoes[i] = { ...opcoes[i], label: e.target.value };
														updateSelectedNodeData({ options: opcoes });
													}}
												/>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
													aria-label={`Remover opção ${i + 1}`}
													onClick={() => {
														// Remover a opção deixa a ligação dela apontando
														// para o vazio; some com ela junto.
														const alvo = `opt-${opcao.number}`;
														setEdges((eds) =>
															eds.filter(
																(e) =>
																	!(
																		e.source === selectedNode.id &&
																		e.sourceHandle === alvo
																	)
															)
														);
														updateSelectedNodeData({
															options: selectedNode.data.options.filter(
																(_, idx) => idx !== i
															),
														});
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
											{/* A lista mostra uma segunda linha por item; os botões não. */}
											{selectedNode.type === "list" && (
												<Input
													className="ml-[3.875rem] w-[calc(100%-5.875rem)]"
													value={opcao.description || ""}
													aria-label={`Descrição da opção ${i + 1}`}
													placeholder="Descrição (opcional)"
													onChange={(e) => {
														const opcoes = [...selectedNode.data.options];
														opcoes[i] = { ...opcoes[i], description: e.target.value };
														updateSelectedNodeData({ options: opcoes });
													}}
												/>
											)}
										</div>
									))}
									<Button
										variant="outline"
										size="sm"
										className="w-full"
										onClick={() => {
											const opcoes = selectedNode.data.options || [];
											updateSelectedNodeData({
												options: [
													...opcoes,
													{ number: String(opcoes.length + 1), label: "" },
												],
											});
										}}
									>
										<Plus className="h-4 w-4" />
										Adicionar opção
									</Button>
								</div>

								{selectedNode.type !== "menu" && (
									<>
										{selectedNode.type === "list" && (
											<>
												<div className="space-y-1.5">
													<Label htmlFor="listTitle">Título da lista</Label>
													<Input
														id="listTitle"
														value={selectedNode.data.listTitle || ""}
														placeholder="Nossos serviços"
														onChange={(e) =>
															updateSelectedNodeData({ listTitle: e.target.value })
														}
													/>
												</div>
												<div className="space-y-1.5">
													<Label htmlFor="listButton">
														Texto do botão que abre a lista
													</Label>
													<Input
														id="listButton"
														value={selectedNode.data.buttonText || ""}
														placeholder="Ver opções"
														onChange={(e) =>
															updateSelectedNodeData({ buttonText: e.target.value })
														}
													/>
												</div>
											</>
										)}

										<div className="space-y-1.5">
											<Label htmlFor="menuFooter">Rodapé</Label>
											<Input
												id="menuFooter"
												value={selectedNode.data.footer || ""}
												placeholder="Opcional, sai em letra menor"
												onChange={(e) =>
													updateSelectedNodeData({ footer: e.target.value })
												}
											/>
										</div>

										<p className="text-xs text-muted-foreground">
											O WhatsApp aceita{" "}
											{selectedNode.type === "list" ? "10 itens" : "3 botões"}. Se o
											aparelho de quem recebe não desenhar o menu, a mesma pergunta
											chega como texto numerado — digitar o número continua
											funcionando.
										</p>
									</>
								)}

								<div className="space-y-1.5">
									<Label htmlFor="menuInvalid">Resposta a escolha inválida</Label>
									<Input
										id="menuInvalid"
										value={selectedNode.data.invalidMessage || ""}
										onChange={(e) =>
											updateSelectedNodeData({ invalidMessage: e.target.value })
										}
									/>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="menuAttempts">Tentativas antes de desistir</Label>
									<Input
										id="menuAttempts"
										type="number"
										min={1}
										value={selectedNode.data.maxAttempts ?? 3}
										onChange={(e) =>
											updateSelectedNodeData({
												maxAttempts: Number(e.target.value),
											})
										}
									/>
									<p className="text-xs text-muted-foreground">
										Passando disso o fluxo segue pela saída “desistiu”.
									</p>
								</div>
							</>
						)}

						{selectedNode.type === "end" && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="endMessage">Despedida (opcional)</Label>
									<Textarea
										id="endMessage"
										rows={3}
										value={selectedNode.data.message || ""}
										placeholder="Obrigado! Se precisar, é só chamar."
										onChange={(e) =>
											updateSelectedNodeData({ message: e.target.value })
										}
									/>
								</div>

								<div className="flex items-center justify-between gap-3 rounded-lg border p-3">
									<div className="space-y-0.5">
										<Label htmlFor="endClose">Encerrar o atendimento</Label>
										<p className="text-xs text-muted-foreground">
											Marca como resolvido em vez de deixá-lo aberto para a equipe.
										</p>
									</div>
									<Switch
										id="endClose"
										checked={Boolean(selectedNode.data.closeTicket)}
										onCheckedChange={(v) =>
											updateSelectedNodeData({ closeTicket: v })
										}
									/>
								</div>

								<p className="text-xs text-muted-foreground">
									Depois daqui o robô se cala neste atendimento: nem este fluxo nem
									a saudação recomeçam a cada mensagem do cliente. Ele volta a valer
									quando o atendimento for encerrado.
								</p>
							</>
						)}

						{selectedNode.type === "question" && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="questionMessage">Pergunta</Label>
									<Textarea
										id="questionMessage"
										rows={3}
										value={selectedNode.data.message || ""}
										onChange={(e) =>
											updateSelectedNodeData({ message: e.target.value })
										}
										placeholder="Qual o seu e-mail?"
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="questionVar">Guardar a resposta em</Label>
									<Input
										id="questionVar"
										value={selectedNode.data.variable || ""}
										onChange={(e) =>
											updateSelectedNodeData({
												// Só letras, números e _: o nome vira uma chave usada
												// como {{email}} nos nós seguintes.
												variable: e.target.value.replace(/[^\w]/g, ""),
											})
										}
										placeholder="email"
									/>
									<p className="text-xs text-muted-foreground">
										Use {"{{"}
										{selectedNode.data.variable || "nome"}
										{"}}"} nos nós seguintes.
									</p>
								</div>

								{/* Sem formato, "num sei" entrava como e-mail e o resto
								    do fluxo seguia com lixo. */}
								<div className="space-y-1.5">
									<Label>Formato esperado</Label>
									<Select
										value={selectedNode.data.answerType || "texto"}
										onValueChange={(v) =>
											updateSelectedNodeData({ answerType: v })
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="texto">Qualquer texto</SelectItem>
											<SelectItem value="numero">Número</SelectItem>
											<SelectItem value="email">E-mail</SelectItem>
											<SelectItem value="telefone">Telefone</SelectItem>
											<SelectItem value="data">Data (dia/mês/ano)</SelectItem>
										</SelectContent>
									</Select>
									<p className="text-xs text-muted-foreground">
										Fora do formato, o robô avisa e pergunta de novo em vez de
										seguir com uma resposta que não serve.
									</p>
								</div>

								{(selectedNode.data.answerType || "texto") !== "texto" && (
									<div className="space-y-1.5">
										<Label htmlFor="questionInvalid">
											Aviso quando não servir
										</Label>
										<Input
											id="questionInvalid"
											value={selectedNode.data.invalidMessage || ""}
											onChange={(e) =>
												updateSelectedNodeData({
													invalidMessage: e.target.value,
												})
											}
											placeholder="Esse e-mail não parece certo. Pode conferir?"
										/>
										<p className="text-xs text-muted-foreground">
											Em branco, o robô usa um aviso padrão.
										</p>
									</div>
								)}
							</>
						)}

						{selectedNode.type === "setVariable" && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="varName">Nome da variável</Label>
									<Input
										id="varName"
										value={selectedNode.data.variable || ""}
										onChange={(e) =>
											updateSelectedNodeData({
												variable: e.target.value.replace(/[^\w]/g, ""),
											})
										}
										placeholder="plano"
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="varValue">Valor</Label>
									<Input
										id="varValue"
										value={selectedNode.data.value || ""}
										onChange={(e) =>
											updateSelectedNodeData({ value: e.target.value })
										}
										placeholder="Premium"
									/>
									<p className="text-xs text-muted-foreground">
										Pode misturar com o que já existe: {"{{nome}}"} vira o que
										foi guardado antes. Depois use {"{{"}
										{selectedNode.data.variable || "plano"}
										{"}}"} nos nós seguintes.
									</p>
								</div>
							</>
						)}

						{selectedNode.type === "jump" && (
							<div className="space-y-1.5">
								<Label>Ir para qual bloco</Label>
								<Select
									value={selectedNode.data.targetNodeId || ""}
									onValueChange={(v) => {
										const alvo = nodes.find((n) => n.id === v);
										updateSelectedNodeData({
											targetNodeId: v,
											targetLabel: alvo ? NODE_LABELS[alvo.type] : "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Escolha o bloco" />
									</SelectTrigger>
									<SelectContent>
										{nodes
											.filter((n) => n.id !== selectedNode.id)
											.map((n) => (
												<SelectItem key={n.id} value={n.id}>
													{NODE_LABELS[n.type] || n.type}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									Serve para repetir um menu depois de uma resposta errada, sem
									desenhar tudo de novo. O fluxo para sozinho depois de 50
									passos, então não trava em laço.
								</p>
							</div>
						)}

						{selectedNode.type === "media" && (
							<>
								<div className="space-y-1.5">
									<Label>Tipo</Label>
									<Select
										value={selectedNode.data.mediaType || "image"}
										onValueChange={(v) => updateSelectedNodeData({ mediaType: v })}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="image">Imagem</SelectItem>
											<SelectItem value="video">Vídeo</SelectItem>
											<SelectItem value="audio">Áudio</SelectItem>
											<SelectItem value="document">Documento</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="mediaUrl">Arquivo</Label>
									<div className="flex gap-2">
										<Input
											id="mediaUrl"
											value={selectedNode.data.url || ""}
											onChange={(e) =>
												updateSelectedNodeData({ url: e.target.value })
											}
											placeholder="Escolha um arquivo ou cole um endereço"
										/>
										<Button
											type="button"
											variant="outline"
											size="icon"
											className="shrink-0"
											disabled={enviandoArquivo}
											aria-label="Enviar arquivo do computador"
											onClick={() => arquivoRef.current?.click()}
										>
											{enviandoArquivo ? (
												<Loader2 className="h-4 w-4 animate-spin" />
											) : (
												<Upload className="h-4 w-4" />
											)}
										</Button>
									</div>
									<input
										ref={arquivoRef}
										type="file"
										className="hidden"
										onChange={(e) => {
											enviarArquivo(e.target.files?.[0], (nome, tipo) =>
												updateSelectedNodeData({
													url: nome,
													mediaType: tipo || selectedNode.data.mediaType,
												})
											);
											e.target.value = "";
										}}
									/>
									<p className="text-xs text-muted-foreground">
										Pode subir do computador ou colar o endereço de um arquivo
										que já está na internet.
									</p>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="mediaCaption">Legenda</Label>
									<Input
										id="mediaCaption"
										value={selectedNode.data.caption || ""}
										onChange={(e) =>
											updateSelectedNodeData({ caption: e.target.value })
										}
									/>
								</div>
							</>
						)}

						{selectedNode.type === "randomizer" && (
							<div className="space-y-1.5">
								<Label htmlFor="randomPercent">Chance do caminho A (%)</Label>
								<Input
									id="randomPercent"
									type="number"
									min={0}
									max={100}
									value={selectedNode.data.percent ?? 50}
									onChange={(e) =>
										updateSelectedNodeData({ percent: Number(e.target.value) })
									}
								/>
								<p className="text-xs text-muted-foreground">
									O resto vai pelo caminho B.
								</p>
							</div>
						)}

						{selectedNode.type === "assignUser" && (
							<div className="space-y-1.5">
								<Label>Atendente</Label>
								<Select
									value={
										selectedNode.data.userId
											? String(selectedNode.data.userId)
											: undefined
									}
									onValueChange={(v) => {
										const alvo = atendentes.find((u) => String(u.id) === v);
										updateSelectedNodeData({
											userId: v,
											userName: alvo?.name || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Escolha o atendente" />
									</SelectTrigger>
									<SelectContent>
										{atendentes.map((u) => (
											<SelectItem key={u.id} value={String(u.id)}>
												{u.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									O atendimento passa para “atendendo” com essa pessoa.
								</p>
							</div>
						)}

						{selectedNode.type === "ticketStatus" && (
							<div className="space-y-1.5">
								<Label>Situação</Label>
								<Select
									value={selectedNode.data.status || "pending"}
									onValueChange={(v) => updateSelectedNodeData({ status: v })}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="pending">Aguardando</SelectItem>
										<SelectItem value="open">Atendendo</SelectItem>
										<SelectItem value="closed">Resolvido</SelectItem>
									</SelectContent>
								</Select>
							</div>
						)}

						{selectedNode.type === "httpRequest" && (
							<>
								<div className="space-y-1.5">
									<Label>Método</Label>
									<Select
										value={selectedNode.data.method || "GET"}
										onValueChange={(v) => updateSelectedNodeData({ method: v })}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="GET">GET</SelectItem>
											<SelectItem value="POST">POST</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="nodeUrl">URL</Label>
									<Input
										id="nodeUrl"
										value={selectedNode.data.url || ""}
										onChange={(e) =>
											updateSelectedNodeData({ url: e.target.value })
										}
										placeholder="https://api.exemplo.com/hook"
									/>
								</div>
								{selectedNode.data.method === "POST" && (
									<div className="space-y-1.5">
										<Label htmlFor="nodeHttpBody">Corpo (JSON)</Label>
										<Textarea
											id="nodeHttpBody"
											rows={4}
											value={selectedNode.data.body || ""}
											onChange={(e) =>
												updateSelectedNodeData({ body: e.target.value })
											}
										/>
									</div>
								)}
							</>
						)}
					</div>
				)}
			</div>

			{/* Simulador de Fluxo interativo */}
			<FlowSimulator
				open={simulatorOpen}
				onClose={() => setSimulatorOpen(false)}
				flowName={name}
				nodes={nodes}
				edges={edges}
			/>

			{/* Painel de execuções */}
			<Sheet open={executionsOpen} onOpenChange={setExecutionsOpen}>
				<SheetContent className="w-full sm:max-w-lg">
					<SheetHeader>
						<SheetTitle className="flex items-center gap-2">
							Execuções
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								onClick={fetchExecutions}
							>
								<RefreshCw
									className={`h-3.5 w-3.5 ${
										loadingExecutions ? "animate-spin" : ""
									}`}
								/>
							</Button>
						</SheetTitle>
					</SheetHeader>
					<div className="mt-4 space-y-3 overflow-y-auto pb-6">
						{executions.length === 0 && !loadingExecutions && (
							<p className="text-sm text-muted-foreground">
								Nenhuma execução registrada ainda.
							</p>
						)}
						{executions.map((execution) => (
							<div key={execution.id} className="rounded-lg border bg-card p-3">
								<div className="flex items-center justify-between gap-2">
									<div className="flex items-center gap-2">
										<Badge variant={statusVariant[execution.status] || "secondary"}>
											{statusLabel[execution.status] || execution.status}
										</Badge>
										<span className="text-xs text-muted-foreground">
											#{execution.id}
										</span>
									</div>
									<span className="text-xs text-muted-foreground">
										{execution.createdAt
											? format(parseISO(execution.createdAt), "dd/MM HH:mm:ss")
											: ""}
									</span>
								</div>
								{(execution.log || []).length > 0 && (
									<ul className="mt-2 space-y-1 border-t pt-2">
										{execution.log.map((entry, index) => (
											<li
												key={`${execution.id}-${index}`}
												className="flex items-start gap-2 text-xs"
											>
												<span
													className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${
														entry.status === "failed"
															? "bg-destructive"
															: entry.status === "skipped"
															? "bg-muted-foreground"
															: "bg-emerald-500"
													}`}
												/>
												<span className="text-muted-foreground">
													<span className="font-medium text-foreground">
														{NODE_LABELS[entry.type] || entry.type}
													</span>{" "}
													— {entry.message}
												</span>
											</li>
										))}
									</ul>
								)}
							</div>
						))}
					</div>
				</SheetContent>
			</Sheet>
		</div>
	);
};

export default FlowBuilder;
